// Components/MessagesArea/hooks/useMessageManager.js
import { useState, useEffect, useRef } from 'react';
import ApiClient from '../../../services/apiClient';
import AuthService from '../../../services/authService';

export const useMessageManager = ({
    messagesUser,
    messagesContainerRef,
    contactActive,
    setIsMessagesReady,
    isMessagesReady
}) => {
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [allMessages, setAllMessages] = useState([]);
    const initialScrollDone = useRef(false);
    const previousContactId = useRef(null);
    const MESSAGES_PER_PAGE = 20;

    // Procesar mensajes iniciales
    useEffect(() => {
        if (messagesUser && Array.isArray(messagesUser) && messagesUser.length > 0) {
            // Asegurarse de que todos los mensajes tengan timestamp como objeto Date
            const processedInitialMessages = messagesUser.map(message => ({
                ...message,
                timestamp: message.timestamp instanceof Date ? message.timestamp : new Date(message.timestamp)
            }));

            // Eliminar duplicados por ID o contenido+timestamp
            const uniqueMessages = [];
            const seenIds = new Set();
            const seenSignatures = new Set();

            processedInitialMessages.forEach(message => {
                // Crear una firma única para mensajes sin ID
                const messageSignature = message.id ||
                    `${message.text}-${message.sender}-${message.timestamp.getTime()}`;

                // Solo añadir si no hemos visto este ID o firma antes
                if (!seenIds.has(message.id) && !seenSignatures.has(messageSignature)) {
                    if (message.id) seenIds.add(message.id);
                    seenSignatures.add(messageSignature);
                    uniqueMessages.push(message);
                }
            });

            // Mantener el orden cronológico (más antiguos arriba, más recientes abajo)
            const sortedMessages = [...uniqueMessages].sort((a, b) =>
                a.timestamp.getTime() - b.timestamp.getTime()
            );

            setAllMessages(sortedMessages);
            setIsMessagesReady(true);
            setPage(1);

            // Determinar si hay más mensajes basado en el número ORIGINAL de mensajes
            setHasMore(processedInitialMessages.length >= MESSAGES_PER_PAGE);

            // Marcar que necesitamos hacer scroll al último mensaje
            initialScrollDone.current = false;
        } else if (messagesUser && Array.isArray(messagesUser) && messagesUser.length === 0) {
            setAllMessages([]);
            setIsMessagesReady(true);
            setPage(1);
            setHasMore(false); // Si no hay mensajes iniciales, probablemente no hay más
        }
    }, [messagesUser, setIsMessagesReady]);

    // Detectar cambios de contacto
    useEffect(() => {
        if (contactActive && contactActive.idContact) {
            // Verificar si cambió el contacto
            if (previousContactId.current !== contactActive.idContact) {
                // Resetear el control de scroll para el nuevo contacto
                initialScrollDone.current = false;
                previousContactId.current = contactActive.idContact;
            }
        }
    }, [contactActive]);

    // Vigilar cambios en contactActive
    useEffect(() => {
        if (contactActive && typeof contactActive === 'object' && contactActive.idContact) {
            setPage(1);
            // El valor de hasMore se establecerá en el efecto que procesa messagesUser
        }
        if (!AuthService.isAuthenticated()) {
            setAllMessages([]);
            setPage(1);
            setHasMore(false);
        }
    }, [contactActive]);

    // Función para cargar más mensajes antiguos desde la API
    const loadMoreMessages = async () => {
        if (!contactActive || !contactActive.idContact || isLoadingMore || !hasMore) return;

        try {
            setIsLoadingMore(true);

            // Hacer petición directamente a la API
            const nextPage = page + 1;
            const olderMessages = await ApiClient.getMessages(
                contactActive.idContact,
                { page: nextPage, limit: MESSAGES_PER_PAGE }
            );

            let processedMessages = [];

            if (olderMessages && olderMessages.data) {
                if (Array.isArray(olderMessages.data)) {
                    processedMessages = olderMessages.data.map(message => ({
                        ...message,
                        timestamp: new Date(message.timestamp)
                    }));
                } else if (typeof olderMessages.data === 'object') {
                    processedMessages = Object.values(olderMessages.data).map(message => ({
                        ...message,
                        timestamp: new Date(message.timestamp)
                    }));
                }
            }

            // Determinar si hay más mensajes
            if (olderMessages.has_more !== undefined) {
                setHasMore(olderMessages.has_more);
            } else if (processedMessages.length < MESSAGES_PER_PAGE) {
                setHasMore(false);
            }

            if (processedMessages.length === 0) {
                setIsLoadingMore(false);
                setHasMore(false);
                return;
            }

            // Filtrar mensajes para evitar duplicados
            const existingMessageIds = new Set(allMessages.map(msg => msg.id));
            const uniqueNewMessages = processedMessages.filter(msg => !existingMessageIds.has(msg.id));

            if (uniqueNewMessages.length === 0) {
                // Si todos los mensajes ya están cargados
                setHasMore(false);
                setIsLoadingMore(false);
                return;
            }

            // Guardar la posición de scroll actual
            const container = messagesContainerRef.current;
            const scrollPosition = container.scrollHeight - container.scrollTop;

            // Agregar los mensajes más antiguos al INICIO del array (preservando orden cronológico)
            setAllMessages(prevMessages => {
                // Unir los nuevos mensajes (más antiguos) con los existentes
                const combined = [...uniqueNewMessages, ...prevMessages];

                // Ordenar cronológicamente
                return combined.sort((a, b) =>
                    a.timestamp.getTime() - b.timestamp.getTime()
                );
            });

            setPage(nextPage);

            // Restaurar la posición de scroll después de que se actualice el DOM
            setTimeout(() => {
                if (container) {
                    container.scrollTop = container.scrollHeight - scrollPosition;
                }
            }, 50);
            
        } catch (error) {
            // console.error('Error al cargar más mensajes:', error);
            setHasMore(false);
            AuthService.checkTokenExpiration(error);
        } finally {
            setIsLoadingMore(false);
        }
    };

    return {
        allMessages,
        hasMore,
        isLoadingMore,
        loadMoreMessages,
        initialScrollDone
    };
};